import { Injectable, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import moment from 'moment';
import get from 'lodash-es/get';
import range from 'lodash-es/range';
import isEmpty from 'lodash-es/isEmpty';
import isNumber from 'lodash-es/isNumber';

import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { GetFullDateStringProvider, GET_FULL_DATE_STRING } from '@finder/shared/utils/string/getFullDateString';
import { GetDateStringProvider, GET_DATE_STRING } from '@finder/shared/utils/string/getDateString';
import formatScheduleTime from '@finder/shared/utils/string/formatScheduleTime';
import { MEAL_PERIOD_CONSTANTS, SCHEDULE_TYPES } from '@finder/shared/constants/app.constants';
import { DATE_FORMAT_CONSTANTS } from '@finder/shared/constants/date.constants';
import { UserSelectionStorage } from '@finder/shared/services/user-selection-storage/user-selection-storage.service';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';

@Injectable({
    providedIn: 'root'
})
export class MealPeriodScheduleService {

    date: Date = new Date();
    fullFormatDate: string = this.getFullDateString(this.date);
    mealPeriodsData: any = {};
    entityId: string;
    isLoading = false;
    selectedTime: string;

    private subject = new Subject<any>();
    get scheduleUpdated() {
        return this.subject.asObservable();
    }

    constructor(
        private finderApi: FinderApiConnectorService,
        private storage: UserSelectionStorage,
        private localeService: LocaleService,
        @Inject(GET_FULL_DATE_STRING) private getFullDateString: GetFullDateStringProvider,
        @Inject(GET_DATE_STRING) private getDateString: GetDateStringProvider,
    ) { }

    init(entityId: string, mealPeriodsData: any, date: Date = this.date) {
        this.entityId = entityId;

        this.initDate(date);
        this.mealPeriodsData = mealPeriodsData;
        this.fullFormatDate = this.getFullDateString(this.date, this.localeService.getLocale());
    }

    set mealPeriodData(data: any) {
        this.mealPeriodsData = data.mealPeriodsData;
        this.isLoading = false;
        this.subject.next(this.mealPeriodsData);
    }

    initDate(date: Date) {
        const savedDate = this.storage.getDate();
        if (savedDate) {
            this.date = savedDate;
            // if there is a date stored by the user we will
            // need to call the services to get schedules for that day
            this.getSchedules(savedDate).subscribe();
        } else {
            this.date = date;
        }
    }

    getSchedules(date: Date = this.date, entityId: string = this.entityId) {
        this.isLoading = true;
        this.entityId = entityId;
        this.date = date;
        this.storage.saveDate(date);
        this.fullFormatDate = this.getFullDateString(this.date, this.localeService.getLocale());
        this.finderApi.getMealPeriodSchedules(entityId, this.getDateString(date))
            .subscribe((data: any) => this.mealPeriodData = data);

        return this.scheduleUpdated;
    }

    getTimes(): any[] {
        const schedules = this.mealPeriodsData && this.mealPeriodsData.schedules;
        const times = [];
        let firstTime;
        let lastTime;

        if (!schedules) {
            return [];
        }

        const possibleMealPeriods = Object.keys(this.mealPeriodsData.schedules[SCHEDULE_TYPES.operating] || {});
        if (possibleMealPeriods.length === 0) {
            return [];
        }

        // Build a list of meal times that are available for the selected day
        Object.keys(this.mealPeriodsData.schedules).forEach(schedule => {
            // Add meal periods first 'Lunch', 'Dinner', etc
            MEAL_PERIOD_CONSTANTS.mealPeriodsOrder.forEach(mealPeriodName => {
                if (schedules[schedule][mealPeriodName] && !firstTime) {
                    firstTime = schedules[schedule][mealPeriodName][0].startTime;
                }
                if (schedules[schedule][mealPeriodName]) {
                    lastTime = schedules[schedule][mealPeriodName][0].endTime;
                    // Make sure the meal time is an option for the day before adding it
                    if (this.mealPeriodsData.mealPeriods[mealPeriodName] &&
                        possibleMealPeriods.includes(mealPeriodName) &&
                        schedule === SCHEDULE_TYPES.operating
                    ) {
                        times.push({
                            'key': this.mealPeriodsData.mealPeriods[mealPeriodName].id,
                            'value': this.mealPeriodsData.mealPeriods[mealPeriodName].value
                        });
                    }
                }
            });
        });

        return times.concat(this.createTimes(firstTime, lastTime));
    }

    /**
     * This method will process the restaurant selected passed by params and
     * it will create a list of schedule times for that restaurant to be used by the times dropdown component.
     *
     * @param selectedRestaurant the restaurant selected.
     */
    getTimeOptions(selectedRestaurant) {
        let times = [];
        const productSchedules = selectedRestaurant && selectedRestaurant.productSchedules;

        // if the selected restaurant doesn't have schedules associated just return an empty list.
        if (!productSchedules) {
            return times;
        }

        // loop the 'Operating' type schedules, format the times and create the times list.
        MEAL_PERIOD_CONSTANTS.mealPeriodsOrder.forEach(mealPeriodName => {
            const schedules = get(productSchedules, `${SCHEDULE_TYPES.operating}.${mealPeriodName}`);
            if (schedules) {
                const timeOptions = schedules.map(schedule => {
                    const startTime = moment(schedule.startTime, DATE_FORMAT_CONSTANTS.timeParseFormat);
                    const newSchedule: any = {
                        key: schedule.startTime,
                        value: startTime.format(DATE_FORMAT_CONSTANTS.timeOutputFormat),
                    };
                    if (schedule.specialNeeds) {
                        newSchedule.specialNeeds = schedule.specialNeeds;
                    }

                    return newSchedule;
                });
                times = times.concat(timeOptions);
            }
        });

        return times;
    }

    /**
     * Gets all the restaurant list of times based on the restaurant schedules.
     *
     * @param selectedRestaurant the restaurant selected.
     * @returns an array of times for a particular restaurant.
     */
    getRestaurantTimes(selectedRestaurant: any): any[]{
        const times = [];
        const schedules = selectedRestaurant &&
            selectedRestaurant.productSchedules &&
            selectedRestaurant.productSchedules[SCHEDULE_TYPES.operating];

        // if the selected restaurant doesn't have schedules just return an empty list.
        if (!schedules) {
            return times;
        }
        let firstTime;
        let lastTime;

        // loop throw the schedules to get the first and last time
        // so we can create a range of times for the times dropdown.
        schedules.forEach(schedule => {
            if (!firstTime) {
                firstTime = schedule.startTime;
                lastTime = schedule.endTime;
            } else {
                firstTime = schedule.startTime < firstTime ? schedule.startTime : firstTime;
                lastTime = schedule.endTime > lastTime ? schedule.endTime : lastTime;
            }
        });

        return times.concat(this.createTimes(firstTime, lastTime));
    }

    /**
     * Return the unavailable message with the start time and end time
     * based on the options the user selected for the availabiltiy search.
     *
     * @param messageTemplate the message template coming from the service response.
     * @param params the params object that can have the following properties:
     * {
     *      searchTime: '12:00:00',
     *      mealPeriod: '1243434',
     *      searchRangeMiliseconds: 1231231, // the search range miliseconds
     *      singleDate: true // if the template will have only one date
     * }
     * @returns the unavailable message.
     */
    getUnavailableTimesMessage(messageTemplate = '', params: any = {}): string {
        const dateText = '{date}';
        const times: any = {};

        /**
         * Calculates start time and end time based on a range
         * of 1.5 hours.
         */
        const getTimeRange = () => {
            const searchTimeMilliseconds = moment(params.searchTime, DATE_FORMAT_CONSTANTS.timeParseFormat).valueOf();
            times.startTime = moment(searchTimeMilliseconds - params.searchRangeMiliseconds)
                .format(DATE_FORMAT_CONSTANTS.timeOutputFormat);
            times.endTime = moment(searchTimeMilliseconds + params.searchRangeMiliseconds)
                .format(DATE_FORMAT_CONSTANTS.timeOutputFormat);
        };

        /**
         * Calculates the start time and end time based on the
         * meal period schedule times.
         */
        const getMealPeriodTime = (mealPeriodsData) => {
            const filteredMealPeriod = Object.keys(mealPeriodsData.mealPeriods)
                .filter(key => mealPeriodsData.mealPeriods[key].id === params.mealPeriod);

            if (!isEmpty(filteredMealPeriod)) {
                const mealPeriodSchedule = get(mealPeriodsData, `schedules.Operating.${filteredMealPeriod[0]}.0`);
                if (mealPeriodSchedule) {
                    times.startTime = formatScheduleTime(mealPeriodSchedule.startTime);
                    times.endTime = formatScheduleTime(mealPeriodSchedule.endTime);
                }
            }
        };

        if (params.singleDate) {
            times.startTime = formatScheduleTime(params.searchTime);
        } else if (params.searchTime) {
            getTimeRange();
        } else if (params.mealPeriod && this.mealPeriodsData.mealPeriods) {
            getMealPeriodTime(this.mealPeriodsData);
        }
        messageTemplate = messageTemplate.replace(dateText, times.startTime);
        messageTemplate = times.endTime ? messageTemplate.replace(dateText, times.endTime) : messageTemplate;

        return messageTemplate;
    }
    saveSelectedTime(time: string) {
        this.storage.saveTime(time);
    }

    getSelectedTime(): string {
        return this.storage.getTime();
    }

    saveMealPeriod(mealPeriodId: string) {
        this.storage.saveMealPeriod(mealPeriodId);
    }

    savePartySize(partySize: number) {
        if (isNumber(partySize)) {
            this.storage.savePartySize(partySize.toString());
        }
    }

    getPartySize() {
        let partySize;
        const savedPartySize = this.storage.getPartySize();
        if (savedPartySize) {
            partySize = parseInt(savedPartySize, 10);
        }

        return partySize;
    }

    /**
     * Create a list of times in ranges of 30 minutes between the startTime and endTime values.
     *
     * @param stratTime the start time
     * @param endTime the end time
     * @returns the list of times.
     * @example
     * Input -> startTime: 15:30:00, endTime: 17:00:00
     * Output -> [
     *  {'key':'3:30 PM', 'value': '3:30 PM'},
     *  {'key':'4 PM', 'value':'4 PM'},
     *  {'key':'4:30 PM','value':'4:30 PM'},
     *  {'key':'5 PM','value':'5 PM'}]
     */
    private createTimes(stratTime, endTime): any[] {
        const times = [];
        if (stratTime && endTime) {
            const momentStart = moment(stratTime, DATE_FORMAT_CONSTANTS.timeParseFormat);
            const momentEnd = moment(endTime, DATE_FORMAT_CONSTANTS.timeParseFormat);
            const rangeStep = 30 * 60 * 1000; // 30 min
            // range method doesn include the end so adding 30 to momentEnd.
            const rangeTimes = range(momentStart.valueOf(), momentEnd.valueOf() + rangeStep, rangeStep);

            rangeTimes.forEach(timeRange => {
                times.push({
                    'key': moment(timeRange).format(DATE_FORMAT_CONSTANTS.timeParseFormat),
                    'value': moment(timeRange).format(DATE_FORMAT_CONSTANTS.timeOutputFormat),
                });
            });
        }

        return times;
    }
}
